import axiosInstance from "./axiosInstance";

/**
 * Field names POST /jobs/ and PUT /jobs/{id} expect. Both hit the same schema.
 *
 * The names are NOT what the form calls them: it's `job_title`, not `title`, and
 * `company_name`, not `company`. The backend reads the body with raw dict access and does
 * no field validation, so a wrong name raises a KeyError server-side and comes back as a
 * generic 500 ("Something went wrong") with the missing key hidden in `details`. There is
 * no "field required" error to read. That makes this class of bug very easy to ship
 * unnoticed — which is exactly what happened — so we check for it here instead.
 */
const REQUIRED_JOB_FIELDS = ["job_title", "company_name", "location", "description"];

const assertJobPayload = (jobData) => {
  const missing = REQUIRED_JOB_FIELDS.filter((f) => jobData?.[f] === undefined);
  if (!missing.length) return;

  const message = `Job payload is missing: ${missing.join(", ")}. The API expects job_title / company_name (not title / company).`;
  // Fail loudly in dev; in prod, log rather than block the user on our own bug.
  if (process.env.NODE_ENV !== "production") throw new Error(message);
  console.error(message, jobData);
};

export const createJobAPI = async (jobData) => {
  assertJobPayload(jobData);
  try {
    const response = await axiosInstance.post("jobs/", jobData);
    return response.data;
  } catch (error) {
    console.error("Error creating job:", error);
    const errorMessage = error.response?.data?.message || "Failed to create job. Please try again.";
    throw new Error(errorMessage);
  }
};

export const fetchJobAPI = async (page = 1, pageSize = 6) => {
  try {
    const response = await axiosInstance.get(`jobs/`, {
      params: {
        page,
        limit: pageSize
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching jobs:", error);
    const errorMessage = error.response?.data?.message || "Failed to fetch jobs. Please try again.";
    throw new Error(errorMessage);
  }
};

// Fetch a single job by id. The rail only lists your OWN jobs, so the console falls back
// to this when it lands on a job that isn't in the rail — e.g. a super_admin opening another
// company's job from the All Jobs page. Returns the job object, or null if not accessible.
export const fetchJobByIdAPI = async (jobId) => {
  try {
    const response = await axiosInstance.get(`jobs/${jobId}`);
    return response.data?.job ?? null;
  } catch (error) {
    console.error(`Error fetching job with ID ${jobId}:`, error);
    return null; // header stays blank; the candidate table loads independently
  }
};

export const updateJobAPI = async (jobId, jobData) => {
  assertJobPayload(jobData);
  try {
    const response = await axiosInstance.put(`jobs/${jobId}`, jobData);
    return response.data;
  } catch (error) {
    console.error(`Error updating job with ID ${jobId}:`, error);
    const errorMessage = error.response?.data?.message || "Failed to update job. Please try again.";
    throw new Error(errorMessage);
  }
};

export const deleteJobAPI = async (jobId) => {
  try {
    const response = await axiosInstance.delete(`jobs/${jobId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting job:", error);
    const errorMessage = error.response?.data?.message || "Failed to delete job. Please try again.";
    throw new Error(errorMessage);
  }
};

export const fetchCompanies = async () => {
  try {
    const response = await axiosInstance.get("companies");
    return response.data;
  } catch (error) {
    console.error("Error fetching companies:", error);
    const errorMessage = error.response?.data?.message || "Failed to fetch companies. Please try again.";
    throw new Error(errorMessage);
  }
};

  export const updateCompany = async (id, payload) => {
    try {
      const response = await axiosInstance.put(`companies/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error updating company with ID ${id}:`, error);
      const errorMessage = error.response?.data?.message || "Failed to update company. Please try again.";
      throw new Error(errorMessage);
    }
  };
  

  export const AdminUsers = async () => {
    try {
      const response = await axiosInstance.get("/admin/users/");
      return response.data;
    } catch (error) {
      console.error("Error fetching Users:", error);
      const errorMessage = error.response?.data?.message || "Failed to fetch users. Please try again.";
      throw new Error(errorMessage);
    }
  };

  export const GetCompanyAdmin = async () => {
    try {
      const response = await axiosInstance.get("/admin/company/");
      return response.data;
    } catch (error) {
      console.error("Error fetching companies:", error);
      const errorMessage = error.response?.data?.message || "Failed to fetch companies. Please try again.";
      throw new Error(errorMessage);
    }
  };

  export const UpdateCompanyAdmin = async (payload) => {
    try {
      const response = await axiosInstance.put("/admin/company/", payload);
      return response.data;
    } catch (error) {
      console.error(`Error updating company`, error);
      const errorMessage = error.response?.data?.message || "Failed to update company. Please try again.";
      throw new Error(errorMessage);
    }
  };

export const SendAdminInvite = async (payload) => {
  try {
    const response = await axiosInstance.post("/admin/invite/", payload);
    return response.data;
  } catch (error) {
    console.error("Error sending invite:", error);
    const errorMessage = error.response?.data?.message || "Failed to send invite. Please try again.";
    throw new Error(errorMessage);
  }
};

/** Cross-tenant, super_admin-only. Every job on the platform.
 *  params: { page, limit, q, company_id, owner_id } — falsy values are dropped so the
 *  request URL carries only the filters actually in effect. */
export const getAllJobs = async ({ page = 1, limit = 25, ...filters } = {}) => {
  try {
    const params = { page, limit };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    const response = await axiosInstance.get("admin/all-jobs", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching all jobs:", error);
    const errorMessage = error.response?.data?.message || "Failed to fetch jobs. Please try again.";
    throw new Error(errorMessage);
  }
};

/** Cross-tenant, super_admin-only. Every user, including deactivated ones and rows with
 *  no company. params: { page, limit, q, company_id, role, is_active }. */
export const getAllUsers = async ({ page = 1, limit = 25, ...filters } = {}) => {
  try {
    const params = { page, limit };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    const response = await axiosInstance.get("admin/all-users", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching all users:", error);
    const errorMessage = error.response?.data?.message || "Failed to fetch users. Please try again.";
    throw new Error(errorMessage);
  }
};

/** Company names for the admin filter dropdowns. Not GET /companies — that one returns
 *  every tenant's merge_company_access_token, which has no business being in a browser. */
export const getAdminCompanies = async () => {
  try {
    const response = await axiosInstance.get("admin/companies");
    return response.data.companies || [];
  } catch (error) {
    console.error("Error fetching companies:", error);
    return [];   // a dead dropdown must not blank the page — the table still works
  }
};
